import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  ConfiguracaoStepper,
  SecaoStepper,
  EtapaStepper,
  EstadoStepper,
  IconeSecaoStepper
} from '../shared/stepper-navigation/interfaces/stepper.interface';
import { EtapasDeEdicaoService, Opcoes } from './etapas-de-edicao/etapas-de-edicao.service';

@Injectable({
  providedIn: 'root'
})
export class StepperEstadoService {
  private carregando = new BehaviorSubject<boolean>(false);
  private erro = new BehaviorSubject<string | undefined>(undefined);
  private rotaResultado: string = '/home';

  constructor(private etapasDeEdicaoService: EtapasDeEdicaoService) {}

  setRotaResultado(rota: string): void {
    this.rotaResultado = rota;
  }

  getRotaResultado(): string {
    return this.rotaResultado;
  }

  // Observables que usam o serviço existente (Sempre que etapas mudam, o stepper atualiza automaticamente)
  obterConfiguracaoAtual(): Observable<ConfiguracaoStepper> {
    return this.etapasDeEdicaoService.getOpcoes().pipe(
      map(opcoes => this.converterOpcoesParaConfiguracao(opcoes))
    );
  }

  obterEtapaAtual(): Observable<number> {
    return this.etapasDeEdicaoService.getOpcaoAtual();
  }

  obterCarregando(): Observable<boolean> {
    return this.carregando.asObservable();
  }

  obterErro(): Observable<string | undefined> {
    return this.erro.asObservable();
  }

  // Métodos para definir valores usando o serviço existente
  definirEtapaAtual(etapaId: number): void {
    this.etapasDeEdicaoService.setOpcaoAtual(etapaId);
  }

  definirCarregando(carregando: boolean): void {
    this.carregando.next(carregando);
  }

  definirErro(erro: string | undefined): void {
    this.erro.next(erro);
  }

  // Métodos de gerenciamento de etapas utilizando o etapasDeEdicaoService
  habilitarEtapa(etapaId: number): void {
    this.etapasDeEdicaoService.habilitarOpcao(etapaId);
  }

  desabilitarEtapa(etapaId: number): void {
    this.etapasDeEdicaoService.desabilitarOpcao(etapaId);
  }

  habilitarSecao(secaoId: number): void {
    this.etapasDeEdicaoService.getOpcoes().pipe(
      take(1) // Pega apenas o primeiro valor e se desinscreve automaticamente
    ).subscribe(opcoes => {
      opcoes
        .filter(opcao => opcao.sectionId === secaoId)
        .forEach(opcao => this.etapasDeEdicaoService.habilitarOpcao(opcao.id));
    });
  }

  desabilitarSecao(secaoId: number): void {
    this.etapasDeEdicaoService.getOpcoes().pipe(
      take(1)
    ).subscribe(opcoes => {
      opcoes
        .filter(opcao => opcao.sectionId === secaoId)
        .forEach(opcao => this.etapasDeEdicaoService.desabilitarOpcao(opcao.id));
    });
  }

  private converterOpcoesParaConfiguracao(opcoes: Opcoes[]): ConfiguracaoStepper {
    // Agrupa opções por sectionId
    const secaoPorId = new Map<number, Opcoes[]>();

    opcoes.filter(opcao => !opcao.oculto).forEach(opcao => {
      if (!secaoPorId.has(opcao.sectionId)) {
        secaoPorId.set(opcao.sectionId, []);
      }
      secaoPorId.get(opcao.sectionId)!.push(opcao);
    });

    // Converte para SecaoStepper
    const secoes: SecaoStepper[] = [];
    secaoPorId.forEach((etapasOpcoes, secaoId) => {
      const secaoStepper = this.criarSecaoStepper(secaoId, etapasOpcoes);
      secoes.push(secaoStepper);
    });

    secoes.sort((a, b) => a.id - b.id);

    return {
      secoes,
      etapaAtualId: 1, // sobrescrito pelo Observable
      rotaResultado: this.rotaResultado,
      rotaInicio: '/home'
    };
  }

  private criarSecaoStepper(secaoId: number, etapasOpcoes: Opcoes[]): SecaoStepper {
    const etapas: EtapaStepper[] = etapasOpcoes.map((opcao) => ({
      id: opcao.id,
      titulo: opcao.title,
      numeroSecao: opcao.sectionNumber,
      desabilitado: opcao.desabilitado,
      rota: opcao.rota,
    }));

    etapas.sort((a, b) => a.numeroSecao - b.numeroSecao);

    const iconesPorSecao = {
      1: {
        padrao: 'assets/ilustrations/walking-orange.png',
        preenchido: 'assets/ilustrations/walking-orange-filled.png',
        desabilitado: 'assets/ilustrations/walking-disabled.png',
      },
      2: {
        padrao: 'assets/ilustrations/medal-orange.png',
        preenchido: 'assets/ilustrations/medal-orange-filled.png',
        desabilitado: 'assets/ilustrations/medal-disabled.png',
      },
      3: {
        padrao: 'assets/ilustrations/building-orange.png',
        preenchido: 'assets/ilustrations/building-orange-filled.png',
        desabilitado: 'assets/ilustrations/building-disabled.png',
      },
    };

    const titulosPorSecao = {
      1: 'Acesso e oferta educacional',
      2: 'Condições de qualidade',
      3: 'Infraestrutura escolar'
    };

    return {
      id: secaoId,
      titulo: titulosPorSecao[secaoId as keyof typeof titulosPorSecao] || `Seção ${secaoId}`,
      icone: iconesPorSecao[secaoId as keyof typeof iconesPorSecao] || {
        padrao: 'assets/ilustrations/default-orange.png',
        preenchido: 'assets/ilustrations/default-orange-filled.png',
        desabilitado: 'assets/ilustrations/default-disabled.png'
      },
      desabilitado: etapas.every(etapa => etapa.desabilitado),
      etapas
    };
  }
}
